/** Shared eligibility and product-selection contract. No device or commerce side effects. */
const JEWELLERY_TYPES = /^(jewellery|jewelry|engagement rings?|wedding bands?|bridal|rings?|necklaces?|earrings?|bracelets?|pendants?|anniversary rings?)$/i;

export function isJewelleryTryOnProduct(product: any): boolean {
  if (!product || /rolex/i.test(String(product.brand || product.vendor || ''))) return false;
  if (Array.isArray(product.tags) && product.tags.some(tag => String(tag).toLowerCase() === 'inquiry-only')) return false;
  const types = String(product.productType || '').split(',').map(value => value.trim());
  // 'Wedding' is a catalogue context, only when paired with a recognized jewellery type.
  const jewelleryTypes = types.filter(value => !/^wedding$/i.test(value));
  return jewelleryTypes.length > 0 && jewelleryTypes.every(value => JEWELLERY_TYPES.test(value));
}

/** Remove a complete jewellery brand prefix without leaving its punctuation or marks. */
export function jewelleryCardDisplayName(value: any, brandValue: any): string {
  const title = String(value || '');
  const brand = String(brandValue || '').trim();
  if (!brand || !title.toLowerCase().startsWith(brand.toLowerCase())) return title;
  const remainder = title.slice(brand.length);
  // A brand must end at a separator: e.g. Raffi must not shorten Raffinement.
  if (remainder && !/^[\s.,:;|·–—\-®™©]/u.test(remainder)) return title;
  const name = remainder.replace(/^[\s®™©]+/u, '')
    .replace(/^(?:\.(?=[®™©\s]|$)|[®™©]|[·,:;|–—-](?=\s|$))+\s*/u, '').trim();
  return name || title;
}

export function jewelleryTryOnInput(query: any): { handle?: string; variant?: string; invalid: boolean } {
  const handle = query?.product;
  const variant = query?.v;
  if (handle === undefined && variant === undefined) return { invalid: false };
  if (typeof handle !== 'string' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle) || handle.length > 255
    || /(?:^|-)rolex(?:-|$)/i.test(handle)) return { invalid: true };
  if (variant !== undefined && (typeof variant !== 'string' || !/^[1-9][0-9]{0,19}$/.test(variant))) return { invalid: true };
  return { handle, variant, invalid: false };
}

/** Same href contract for native Edge and dynamically rendered collection cards. */
export function jewelleryTryOnHref(handle: any, variant: any, locale = 'en'): string {
  const id = typeof variant === 'string' || typeof variant === 'number'
    ? String(variant).replace(/^gid:\/\/shopify\/ProductVariant\//, '') : undefined;
  const input = jewelleryTryOnInput({ product: handle, v: id });
  if (input.invalid || !input.handle || !input.variant) return '';
  return `${locale === 'fr' ? '/fr' : ''}/jewellery-try-on?product=${input.handle}&v=${input.variant}`;
}

function imageUrl(value: any): string | undefined {
  if (typeof value !== 'string') return undefined;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'https:' || parsed.hostname !== 'cdn.shopify.com' || parsed.username || parsed.password) return undefined;
    return parsed.href;
  } catch { return undefined; }
}

/** The requested variant must belong to this product; never silently substitute another. */
export function jewelleryTryOnSelection(product: any, requestedVariant?: string, locale = 'en') {
  if (!isJewelleryTryOnProduct(product)) return null;
  const input = jewelleryTryOnInput({ product: product.handle, v: requestedVariant });
  if (input.invalid) return null;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const numericId = (value: any) => String(value || '').match(/^(?:gid:\/\/shopify\/ProductVariant\/)?([1-9][0-9]{0,19})$/)?.[1];
  const variant = requestedVariant
    ? variants.find(item => numericId(item.id) === requestedVariant)
    : product.selectedVariant || variants[0];
  const id = numericId(variant?.id);
  if (!variant || !id || !variants.some(item => numericId(item.id) === id)) return null;
  const picture = [variant.image, product.featuredImage, ...(product.images || [])]
    .map(item => imageUrl(item?.url || item?.originalUrl)).find(Boolean);
  const prefix = locale === 'fr' ? '/fr' : '';
  const price = !product.hidePrice && Number.isFinite(Number(variant.rawPrice)) && Number(variant.rawPrice) > 0
    && typeof variant.price === 'string' ? variant.price : '';
  const variantTitle = typeof variant.title === 'string' && !/^(default title|titre par défaut)$/i.test(variant.title) ? variant.title : '';
  return {
    title: String(product.title || ''), brand: String(product.brand || product.vendor || ''),
    variantTitle, reference: String(variant.sku || ''), price, image: picture || '',
    imageAlt: String(variant.image?.alt || product.featuredImage?.alt || product.title || ''),
    productUrl: `${prefix}/products/${input.handle}?v=${id}`,
  };
}
